// src/components/ReadjudicacionMasiva/CrearLote/FiltrosBusquedaLote.js

import React, { useState, useEffect } from 'react';
import makeStyles from '@mui/styles/makeStyles';
import {
    Grid,
    TextField,
    Button,
    Paper,
    Typography,
    Box,
    CircularProgress,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Chip,
    Checkbox,
    ListItemText
} from '@mui/material';
import {
    Search as SearchIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { useSnackbar } from 'notistack';

import busquedaService from '../../../services/busquedaService';
import BuscadorAbogados from './BuscadorAbogados';
import { getCombosJWT } from '../../../api/endpoints';

const useStyles = makeStyles((theme) => ({
    paper: {
        padding: theme.spacing(3)
    },
    section: {
        marginBottom: theme.spacing(3)
    },
    sectionTitle: {
        fontWeight: 600,
        marginBottom: theme.spacing(2),
        color: theme.palette.primary.main
    },
    searchButton: {
        minWidth: 200,
        padding: theme.spacing(1.5, 4),
        fontWeight: 600
    }
}));

const FiltrosBusquedaLote = ({ filtros, onFiltrosChange, onBuscar }) => {
    const classes = useStyles();
    const { enqueueSnackbar } = useSnackbar();
    const [loading, setLoading] = useState(false);
    const [estadosProceso, setEstadosProceso] = useState([]);

    // Caracteres de abogado: 3=Apoderado, 8=Coapoderado
    const caracteresAbogado = [
        { id: 3, nombre: 'Apoderado' },
        { id: 8, nombre: 'Coapoderado' }
    ];

    // Módulos de ejecución
    const modulosEjecucion = [
        { id: 'tributarias', nombre: 'Ejecuciones Tributarias' },
        { id: 'no_tributarias', nombre: 'Ejecuciones No Tributarias' }
    ];

    // Cargar estados de proceso al montar
    useEffect(() => {
        const cargarEstados = async () => {
            try {
                const response = await getCombosJWT('estados_proceso', '', 200);
                if (response.ok && response.items) {
                    setEstadosProceso(response.items.map(item => ({
                        id: item.id,
                        nombre: item.label
                    })));
                }
            } catch (error) {
                console.error('Error al cargar estados:', error);
            }
        };
        cargarEstados();
    }, []);

    const handleChange = (campo) => (event) => {
        onFiltrosChange({
            ...filtros,
            [campo]: event.target.value
        });
    };

    const handleDateChange = (campo) => (date) => {
        onFiltrosChange({
            ...filtros,
            [campo]: date
        });
    };

    const handleBuscar = async () => {
        // Validar que el módulo esté seleccionado (obligatorio)
        if (!filtros.tipoEjecucion) {
            enqueueSnackbar('Debe seleccionar un Módulo', { variant: 'warning' });
            return;
        }

        // Validar que al menos un filtro esté completo
        const tieneFiltros = filtros.fechaAsignacionDesde || filtros.fechaInicioDesde ||
                            filtros.montoDesde || filtros.abogado;

        if (!tieneFiltros) {
            enqueueSnackbar('Debe seleccionar al menos un criterio de búsqueda', { variant: 'warning' });
            return;
        }

        setLoading(true);
        try {
            // Construir objeto de filtros para el servicio
            const filtrosBusqueda = {};

            if (filtros.fechaAsignacionDesde) {
                filtrosBusqueda.fechaAsignacionDesde = filtros.fechaAsignacionDesde;
            }
            if (filtros.fechaAsignacionHasta) {
                filtrosBusqueda.fechaAsignacionHasta = filtros.fechaAsignacionHasta;
            }
            if (filtros.fechaInicioDesde) {
                filtrosBusqueda.fechaInicioDesde = filtros.fechaInicioDesde;
            }
            if (filtros.fechaInicioHasta) {
                filtrosBusqueda.fechaInicioHasta = filtros.fechaInicioHasta;
            }
            if (filtros.montoDesde) {
                filtrosBusqueda.montoDesde = filtros.montoDesde;
            }
            if (filtros.montoHasta) {
                filtrosBusqueda.montoHasta = filtros.montoHasta;
            }
            if (filtros.abogado) {
                // Pasar el ID del abogado para búsqueda por Personal.Id
                filtrosBusqueda.idPersonal = filtros.abogado.Id;
            }
            if (filtros.caracterPersona) {
                filtrosBusqueda.caracterPersona = filtros.caracterPersona;
            }
            if (filtros.tipoEjecucion) {
                filtrosBusqueda.tipoEjecucion = filtros.tipoEjecucion;
            }
            if (filtros.estadosIncluir && filtros.estadosIncluir.length > 0) {
                filtrosBusqueda.estadosProceso = filtros.estadosIncluir;
            }

            const response = await busquedaService.buscarExpedientes(filtrosBusqueda, 1);

            if (response.ok && response.expedientes) {
                // Solo el conteo. El aviso de que la tabla muestra menos que el total, y
                // el botón para seleccionar todos, viven en el paso 2 (ResultadosSeleccion),
                // que es donde el usuario decide: acá era un cartel naranja redundante que
                // además sobrevivía al cambio de pantalla.
                const traidos = response.expedientes.length;
                const total = typeof response.total === 'number' ? response.total : traidos;

                enqueueSnackbar(
                    `Se encontraron ${total.toLocaleString('es-AR')} expedientes`,
                    { variant: 'success' }
                );
                // Se pasan también el total real y los filtros usados, para que el paso
                // de selección pueda ofrecer "seleccionar todos los que coinciden".
                onBuscar(response.expedientes, { total, filtros: filtrosBusqueda });
            } else {
                enqueueSnackbar(response.mensaje || 'No se encontraron expedientes', { variant: 'info' });
                onBuscar([], { total: 0, filtros: filtrosBusqueda });
            }
        } catch (error) {
            console.error('Error al buscar expedientes:', error);
            enqueueSnackbar('Error al buscar expedientes', { variant: 'error' });
            onBuscar([], { total: 0, filtros: null });
        } finally {
            setLoading(false);
        }
    };

    const handleKeyPress = (event) => {
        if (event.key === 'Enter') {
            handleBuscar();
        }
    };

    return (
        <Paper className={classes.paper} onKeyPress={handleKeyPress}>
            <Box className={classes.section}>
                <Typography variant="h6" className={classes.sectionTitle}>
                    Criterios de Búsqueda
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                    Complete al menos un criterio para buscar expedientes judiciales.
                    Busque por abogado para encontrar causas a readjudicar.
                </Typography>
            </Box>

            <Grid container spacing={3}>
                {/* Módulo */}
                <Grid size={{ xs: 12, md: 6 }}>
                    <FormControl variant="outlined" fullWidth size="small">
                        <InputLabel>Módulo *</InputLabel>
                        <Select
                            value={filtros.tipoEjecucion || ''}
                            onChange={(e) => {
                                onFiltrosChange({
                                    ...filtros,
                                    tipoEjecucion: e.target.value
                                });
                            }}
                            label="Módulo *"
                        >
                            {modulosEjecucion.map((modulo) => (
                                <MenuItem key={modulo.id} value={modulo.id}>
                                    {modulo.nombre}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>

                {/* Estados a Incluir */}
                <Grid size={{ xs: 12, md: 6 }}>
                    <FormControl variant="outlined" fullWidth size="small">
                        <InputLabel id="incluir-estados-label">Solo Estados</InputLabel>
                        <Select
                            labelId="incluir-estados-label"
                            multiple
                            value={filtros.estadosIncluir || []}
                            onChange={(e) => {
                                onFiltrosChange({
                                    ...filtros,
                                    estadosIncluir: e.target.value
                                });
                            }}
                            label="Solo Estados"
                            renderValue={(selected) => (
                                <Box display="flex" flexWrap="wrap" style={{ gap: 4 }}>
                                    {selected.map((id) => {
                                        const estado = estadosProceso.find(est => est.id === id);
                                        return (
                                            <Chip
                                                key={`chip-${id}`}
                                                label={estado ? estado.nombre : id}
                                                size="small"
                                                style={{ height: 20 }}
                                            />
                                        );
                                    })}
                                </Box>
                            )}
                        >
                            {estadosProceso.map((estado) => (
                                <MenuItem key={`estado-${estado.id}`} value={estado.id}>
                                    <Checkbox checked={(filtros.estadosIncluir || []).indexOf(estado.id) > -1} />
                                    <ListItemText primary={estado.nombre} />
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>

                {/* Fechas de Asignación */}
                <Grid size={{ xs: 12, md: 6 }}>
                    <DatePicker
                        label="Fecha Asignación Desde"
                        format="dd/MM/yyyy"
                        value={filtros.fechaAsignacionDesde}
                        onChange={handleDateChange('fechaAsignacionDesde')}
                        slotProps={{
                            textField: {
                                variant: "outlined",
                                size: "small",
                                fullWidth: true
                            }
                        }}
                    />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                    <DatePicker
                        label="Fecha Asignación Hasta"
                        format="dd/MM/yyyy"
                        value={filtros.fechaAsignacionHasta}
                        onChange={handleDateChange('fechaAsignacionHasta')}
                        minDate={filtros.fechaAsignacionDesde}
                        slotProps={{
                            textField: {
                                variant: "outlined",
                                size: "small",
                                fullWidth: true
                            }
                        }}
                    />
                </Grid>

                {/* Fechas de Inicio */}
                <Grid size={{ xs: 12, md: 6 }}>
                    <DatePicker
                        label="Fecha Inicio Desde"
                        format="dd/MM/yyyy"
                        value={filtros.fechaInicioDesde}
                        onChange={handleDateChange('fechaInicioDesde')}
                        slotProps={{
                            textField: {
                                variant: "outlined",
                                size: "small",
                                fullWidth: true
                            }
                        }}
                    />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                    <DatePicker
                        label="Fecha Inicio Hasta"
                        format="dd/MM/yyyy"
                        value={filtros.fechaInicioHasta}
                        onChange={handleDateChange('fechaInicioHasta')}
                        minDate={filtros.fechaInicioDesde}
                        slotProps={{
                            textField: {
                                variant: "outlined",
                                size: "small",
                                fullWidth: true
                            }
                        }}
                    />
                </Grid>

                {/* Montos */}
                <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                        label="Monto Desde"
                        type="number"
                        value={filtros.montoDesde}
                        onChange={handleChange('montoDesde')}
                        variant="outlined"
                        fullWidth
                        size="small"
                        InputProps={{
                            startAdornment: <Typography variant="body2">$</Typography>,
                        }}
                    />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                    <TextField
                        label="Monto Hasta"
                        type="number"
                        value={filtros.montoHasta}
                        onChange={handleChange('montoHasta')}
                        variant="outlined"
                        fullWidth
                        size="small"
                        InputProps={{
                            startAdornment: <Typography variant="body2">$</Typography>,
                        }}
                    />
                </Grid>

                {/* Abogado */}
                <Grid size={12}>
                    <Typography variant="subtitle2" gutterBottom style={{ fontWeight: 600, marginTop: 16 }}>
                        Abogado de la Institución
                    </Typography>
                </Grid>
                <Grid size={{ xs: 12, md: 8 }}>
                    <BuscadorAbogados
                        value={filtros.abogado || null}
                        onChange={(abogado) => {
                            onFiltrosChange({
                                ...filtros,
                                abogado: abogado
                            });
                        }}
                        label="Nombre del Abogado"
                        placeholder="Escribe nombre, apellido o matrícula..."
                    />
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                    <FormControl variant="outlined" fullWidth size="small">
                        <InputLabel>Carácter</InputLabel>
                        <Select
                            value={filtros.caracterPersona || 'ambos'}
                            onChange={(e) => {
                                onFiltrosChange({
                                    ...filtros,
                                    caracterPersona: e.target.value === 'ambos' ? null : e.target.value
                                });
                            }}
                            label="Carácter"
                        >
                            <MenuItem value="ambos">
                                Ambos (Apoderado y Coapoderado)
                            </MenuItem>
                            {caracteresAbogado.map((car) => (
                                <MenuItem key={car.id} value={car.id}>
                                    {car.nombre}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>

                {/* Botón de búsqueda */}
                <Grid size={12}>
                    <Box display="flex" justifyContent="center" marginTop={2}>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleBuscar}
                            disabled={loading}
                            className={classes.searchButton}
                            startIcon={loading ? <CircularProgress size={20} /> : <SearchIcon />}
                        >
                            {loading ? 'Buscando...' : 'Buscar Expedientes'}
                        </Button>
                    </Box>
                </Grid>
            </Grid>
        </Paper>
    );
};

export default FiltrosBusquedaLote;
